import React, { ReactNode, useEffect, useState } from "react";
import { Resource, Scope } from "../../../../../../types/NendaTypes";
import PermissionsGate from "../../../components/PermissionGate";
import { t } from "i18next";
import { BasicTable } from "../../../../Table/BasicTable";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffectNotOnMount } from "../../../../../hooks/useEffectNotOnMount";
import { FaTag } from "react-icons/fa";
import { Form, Formik, Field } from "formik";
import { Modal } from "../../../../Modal/Modal";
import { store } from "../../../store";
import {
  CreateTagAction,
  getPaginatorSelector,
  GetTagsAction,
  getTagSelector,
  getTagsSelector,
} from "../../../store/reducers/cmsReducer";
import CMSPageHeader from "../../../ui-components/pageLayout/CMSPageHeader";
import { ModalWrapper } from "../../../ui-components/pageLayout/ModalComponents";
import { TagsPageTableSettings } from "./TagsTableSettings";
import { Select, MenuItem } from "@mui/material";

interface CreateTagValues {
  name: string;
  type: string;
}

const TAG_TYPE_OPTIONS = [
  { value: "other", label: "Other" },
  { value: "provider", label: "Provider" },
  { value: "genre", label: "Genre" },
  { value: "logo", label: "Logo" },
];

const CreateTagSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required*"),
  type: Yup.string()
    .oneOf(["other", "provider", "genre", "logo"])
    .required("Required*"),
});

const Tags: React.FC = (): ReactNode => {
  const [showForm, setShowForm] = React.useState(false);
  const tags = useSelector(getTagsSelector);
  const tag = useSelector(getTagSelector);
  const pagination = useSelector(getPaginatorSelector);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(25);
  const history = useNavigate();

  const fetchTags = (query: string = "") => {
    store.dispatch(GetTagsAction(query));
  };

  useEffect(() => {
    fetchTags("page=1&pageSize=25");
  }, []);

  function createTagSubmit(values: CreateTagValues) {
    setIsCreating(true);
    store.dispatch(CreateTagAction(values));
  }

  const handleRowClick = (id: string) => {
    redirect(id);
  };

  const buildQuery = (page: number, pageSize: number, search: string) => {
    let query = `page=${page}&pageSize=${pageSize}`;
    if (search) query += `&name=${search}`;
    return query;
  };

  const handlePagination = (page: number) => {
    setCurrentPage(page);
    fetchTags(buildQuery(page, currentRowsPerPage, search));
  };

  const handleRowsPerPage = (rowsPerPage: number) => {
    setCurrentPage(1);
    setCurrentRowsPerPage(rowsPerPage);
    fetchTags(buildQuery(1, rowsPerPage, search));
  };

  const handleSearch = (search: string) => {
    setSearch(search);
    setCurrentPage(1);
    fetchTags(buildQuery(1, currentRowsPerPage, search));
  };

  useEffectNotOnMount(() => {
    if (tag && isCreating) {
      redirect(tag.getUniqueId());
    }
  }, [tag]);

  const redirect = (id) => {
    history(`/admin/metadata/tags/${id}`);
  };

  return (
    <PermissionsGate
      restriction={{
        resource: Resource.Content,
        scopes: [Scope.CanAdministrate],
      }}
    >
      <>
        <CMSPageHeader
          title={t("customerportal.pages.tags.title")}
          count={pagination.total}
          button={{
            title: t("customerportal.pages.tags.create"),
            onClick: () => setShowForm(true),
            icon: FaTag,
          }}
          onSearch={handleSearch}
        />
        <Formik
          initialValues={{
            name: "",
            type: "other",
          }}
          validationSchema={CreateTagSchema}
          onSubmit={(values: CreateTagValues) => {
            createTagSubmit(values);
            setShowForm(false);
          }}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <Form>
              <ModalWrapper>
                <Modal
                  visible={showForm}
                  onClose={() => setShowForm(false)}
                  title={"Create new tag / genre / provider"}
                  onSubmit={handleSubmit}
                  confirmText="Create"
                  closeOnSubmit={false}
                >
                  <div className="input-group my-1">
                    <div className="input-group-prepend">
                      <span className="input-group-text">Name</span>
                    </div>
                    <Field
                      name="name"
                      placeholder="Name..."
                      className="form-control"
                    />
                  </div>
                  <div className="input-group my-1">
                    <div className="input-group-prepend">
                      <span className="input-group-text">Type</span>
                    </div>
                    <Select
                      onChange={(e) => setFieldValue("type", e.target.value)}
                      value={values.type}
                      className="form-control"
                    >
                      {TAG_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </Modal>
              </ModalWrapper>
            </Form>
          )}
        </Formik>
        <BasicTable
          tableSettings={TagsPageTableSettings}
          tableData={tags || []}
          onRowClick={handleRowClick}
          paginationControls={{
            paginationCount: pagination.total,
            handlePagination: handlePagination,
            handleRowsPerPage: handleRowsPerPage,
            currentPage: currentPage,
            currentRowsPerPage: currentRowsPerPage,
          }}
        />
      </>
    </PermissionsGate>
  );
};

export default Tags;
